import { Component } from 'react';

import {
  Button,
  CustomSelect,
  FormSubmissionPage,
  FormValidationError,
  Label,
  Loader,
  TextInput,
  Title,
  Tooltip
} from '@ce-cfbdigital/cgl-cfb-fe-foundation';
import DOMPurify from 'dompurify';
import { Formik } from 'formik';
import log from 'loglevel';
import moment from 'moment';
import PropTypes from 'prop-types';
import PubSub from 'pubsub-js';
import * as Yup from 'yup';

import CustomIcon from '../../../common/CustomIcons';
import { SELECTED_ACCOUNT, SELECTED_REGION } from '../../../common/Header/constants';
import { getAppConfig, updateSessionStorage } from '../../../common/utilities';
import * as types from '../../../common/utilities/constants';
import { SUPPORT_TEAMS, SUPPORT_TOPICS } from './constants';

import './styles/index.css';

export class AskAQuestionForm extends Component {
  /**
   *  @constructor
   *  @memberof AskAQuestionForm
   */
  constructor(props) {
    super(props);

    /**
     * @property state
     * @description Defined states of component that can be locally updated
     * @type {{object}}
     */
    this.state = {
      formState: {
        regions: '',
        productSegments: '',
        countries: '',
        supportTopics: '',
        email: '',
        secondaryEmail: '',
        supportTeams: '',
        phone: '',
        comments: '',
        priority: '',
        orderNumber: '',
        purchaseOrderNumber: ''
      },
      formOptions: [
        {
          id: 'regions',
          disable: true,
          value: '',
          options: []
        },
        {
          id: 'productSegments',
          disable: true,
          value: '',
          options: []
        },
        {
          id: 'countries',
          disable: true,
          value: '',
          options: []
        },
        {
          id: 'supportTopics',
          disable: true,
          value: '',
          options: []
        },
        {
          id: 'supportTeams',
          disable: true,
          value: '',
          options: []
        }
      ],
      selectedPriority: '',
      selectedCountry: null,
      formSubmitted: false,
      isLoading: false,
      isSuccess: true,
      isWebToCase: false,
      showPriority: true
    };

    this.productSegmentOptions = [];
  }

  /**
   * @method componentDidMount
   * @description React life cycle method that Calls initially when virtual DOM is ready
   */
  async componentDidMount() {
    const { leapRequestId } = this.props;
    this.setLoader(true);
    await this.setUserEmail();
    if (!leapRequestId) {
      await this.getSupportFormData();
    }
  }

  /**
   * @method onSubmit
   * @description construct payload that need to send in general support pagesubmit  API and trigger analytics
   * call acccording to ssubmit api response
   * @returns success screen on succcess and error sccreen on API error
   */
  onSubmit = values => {
    const { leapRequestId } = this.props;
    if (leapRequestId) {
      this.followUpPayload(values);
    } else {
      this.generalSupportPayload(values);
    }
  };

  /**
   * @method getSupportFormData
   * @description update component state to display initial load screen
   * @returns updated state of component having regions and email
   */
  getSupportFormData = async () => {
    const {
      Get,
      urls,
      userContext,
      supportTopics: { options: supportTopicOptions = [] },
      productSegment: { options: productSupportOptions = [] }
    } = this.props;

    const { generalSupportUrl } = urls;
    const {
      data: { regions = [] }
    } = await Get(generalSupportUrl, true);
    const updatedRegions = regions.map(region => ({
      ...region,
      productSegments: region.productSegments.map(segment => {
        const productSupportOptionsLength = productSupportOptions.length;
        if (productSupportOptionsLength) {
          for (let i = 0; i < productSupportOptionsLength; i += 1) {
            const option = productSupportOptions[i];
            if (option.value === segment.value) {
              return {
                ...segment,
                label: option.label,
                value: option.label,
                countries: segment.countries.map(country => ({
                  ...country,
                  supportTopics: country.supportTopics.map(supportTopic => {
                    const supportTopicOptionsLength = supportTopicOptions.length;
                    if (supportTopicOptionsLength) {
                      for (let j = 0; j < supportTopicOptionsLength; j += 1) {
                        const element = supportTopicOptions[j];
                        if (element.value === supportTopic.value) {
                          return {
                            ...supportTopic,
                            label: element.label,
                            value: element.label
                          };
                        }
                      }
                    }
                    return supportTopic;
                  })
                }))
              };
            }
          }
        }
        return {
          ...segment,
          countries: segment.countries.map(country => ({
            ...country,
            supportTopics: country.supportTopics.map(supportTopic => {
              if (supportTopicOptions.length) {
                for (let i = 0; i < supportTopicOptions.length; i += 1) {
                  const element = supportTopicOptions[i];
                  if (element.value === supportTopic.value) {
                    return {
                      ...supportTopic,
                      label: element.label,
                      value: element.label
                    };
                  }
                }
              }
              return supportTopic;
            })
          }))
        };
      })
    }));
    this.setState(
      prevState => ({
        ...prevState,
        formOptions: prevState.formOptions.map(item => {
          if (item.id === 'regions') {
            return {
              ...item,
              disable: false,
              options: updatedRegions,
              value: updatedRegions && updatedRegions.length === 1 ? updatedRegions[0].value : ''
            };
          }
          return item;
        }),
        formState: { ...prevState.formState, phone: userContext.phone }
      }),
      () => this.setLoader(false)
    );
  };

  setUserEmail = async () => {
    const { getUserInfoFromOkta } = this.props;
    const { email: userEmail } = await getUserInfoFromOkta();
    this.setState(
      prevState => ({
        ...prevState,
        formState: { ...prevState.formState, email: userEmail }
      }),
      () => this.setLoader(false)
    );
  };

  /**
   * @method setLoader
   * @description enable and disable loader
   * @returns updaated state of page level loader
   */
  setLoader = status => this.setState({ isLoading: status });

  getConditionalClass = (item, conditionalClass) => {
    if (item.id === SUPPORT_TEAMS) {
      return `form-input-col-2 ${conditionalClass}`;
    }
    return 'form-input-col-2';
  };

  /**
   * @method getErrorInfo
   * @description This function is used to return the error in case present
   * @returns error or empty string
   */
  getErrorInfo = (key, errors) => (errors[key] ? 'error' : '');

  getTextInput = (id, values, errors, placeholder, maxLength, setFieldValue, disable = false) => (
    <TextInput
      id={id}
      type="text"
      maxLength={maxLength}
      value={values[id]}
      placeholder={placeholder}
      onChange={e => setFieldValue(id, e.target.value, false)}
      inputClassName={`question-form-input-field order-number-field ${errors[id] ? 'error' : ''}`}
      disabled={disable}
    />
  );

  getFormFields = (errors, setFieldValue, renderChildData) => {
    const { labels } = renderChildData;
    const { formOptions, isWebToCase, showPriority } = this.state;
    const conditionalClass = isWebToCase ? 'show-field' : 'hide-field';
    const { country = '' } = getAppConfig('locale');

    return (
      formOptions &&
      formOptions.map(item => {
        if (this.checkIsWebToCase(item, isWebToCase, showPriority)) {
          let props = {
            id: item.id,
            options: item.options,
            isDisabled: item.disable,
            classes: `question-form-select ${this.getErrorInfo(item.id, errors)}`,
            value: { label: item.value || 'Select...', value: item.value },
            onChange: selectedOption => {
              this.handleDropdownChange(selectedOption, item.id, setFieldValue);
              setFieldValue(item.id, selectedOption.value, false);
            }
          };
          if (country.toLowerCase() === types.BRAZIL) {
            if (item.id === 'regions' && item.options.length) {
              const { options } = item;
              const { label, value, productSegments } = options[2];
              this.productSegmentOptions = [...productSegments];
              props = {
                id: item.id,
                options: item.options,
                isDisabled: item.disable,
                classes: `question-form-select ${this.getErrorInfo(item.id, errors)}`,
                onChange: selectedOption => {
                  this.handleDropdownChange(selectedOption, item.id, setFieldValue);
                  setFieldValue(item.id, selectedOption.value, false);
                },
                defaultValue: { label, value }
              };
            }
            if (item.id === 'productSegments') {
              props = {
                id: item.id,
                options: this.productSegmentOptions,
                classes: `question-form-select ${this.getErrorInfo(item.id, errors)}`,
                onChange: selectedOption => {
                  this.handleDropdownChange(selectedOption, item.id, setFieldValue);
                  setFieldValue(item.id, selectedOption.value, false);
                }
              };
            }
          } else {
            this.productSegmentOptions = [];
          }
          return (
            <div className={this.getConditionalClass(item, conditionalClass)} key={item.id}>
              <Label className="question-form-input-label required" htmlFor={labels[item.id]}>
                {labels[item.id]}
              </Label>
              <CustomSelect {...props} />
              {errors[item.id] && <FormValidationError errorMessage={errors[item.id]} />}
            </div>
          );
        }
        return null;
      })
    );
  };

  handleFormSucess = () => {
    const { leapRequestId, handleCloseModal, tabValue, getFollowUpData } = this.props;
    getFollowUpData();
    if (leapRequestId) {
      const isOpenOrClose = tabValue ? types.MY_REQUESTS_CLOSED_TAB : types.MY_REQUESTS_OPEN_TAB;
      const analyticsData = {
        description: types.MY_REQUESTS_SUPPORT_PROCESS_COMPLETE_DESCP.replace('{##}', isOpenOrClose),
        pageName: types.MY_REQUESTS_SUPPORT_URL.replace('{##}', isOpenOrClose),
        capability: types.MY_REQUESTS_CAPABILITY,
        process: { name: types.MY_REQUESTS_PROCESS_NAME },
        event: types.MY_REQUESTS_PROCESS_COMPLETE,
        form: { transactionID: leapRequestId }
      };
      PubSub.publish(types.MY_REQUESTS_ANALYTICS, analyticsData);
    }
    handleCloseModal();
  };

  handleFormCancel = () => {
    const { leapRequestId, handleCloseModal, tabValue } = this.props;
    if (leapRequestId) {
      const isOpenOrClose = tabValue ? types.MY_REQUESTS_CLOSED_TAB : types.MY_REQUESTS_OPEN_TAB;
      const analyticsData = {
        description: types.MY_REQUESTS_FORM_CANCEL_DESCP.replace('{##}', isOpenOrClose),
        pageName: types.MY_REQUESTS_SUPPORT_URL.replace('{##}', isOpenOrClose),
        capability: types.MY_REQUESTS_CAPABILITY,
        element: { name: types.MY_REQUESTS_FORM_CANCEL_EVENT },
        process: { name: types.MY_REQUESTS_PROCESS_NAME },
        event: types.MY_REQUESTS_CLICK_ACTION,
        form: { transactionID: leapRequestId }
      };
      PubSub.publish(types.MY_REQUESTS_ANALYTICS, analyticsData);
    }
    handleCloseModal();
  };

  generalSupportPayload = values => {
    const account = sessionStorage.getItem('selectedAccount');
    let accountName;
    let accountId;
    if (account) {
      try {
        const parsedAccount = JSON.parse(account);
        accountName = parsedAccount.fullname;
        accountId = parsedAccount.globalId;
      } catch (err) {
        log.warn('Failure parsing account name.');
      }
    }

    const {
      regions: region,
      countries: country,
      productSegments: productSegment,
      supportTopics: supportTopic,
      phone,
      comments,
      orderNumber,
      purchaseOrderNumber,
      priority,
      email,
      secondaryEmail,
      supportTeams = ''
    } = values;
    const { formOptions, isWebToCase } = this.state;
    const { value: selectedSupportTopic, options } = formOptions.find(option => option.id === 'supportTopics');
    const { value: supportEmailId } = formOptions.find(item => item.id === SUPPORT_TEAMS);
    const { routedType } = options.find(item => item.value === selectedSupportTopic);
    const payload = {
      accountName,
      requestorEmailId: email,
      secondaryEmail,
      supportEmailId: isWebToCase ? '' : supportEmailId,
      region: region || 'LATAM',
      productSegment,
      supportTopic,
      phoneNumberAltr: phone || '',
      comments,
      routedType,
      orderNumber,
      purchaseOrderNumber,
      priority,
      webToCaseTeam: supportTeams,
      country,
      accountId
    };
    this.setState({
      isLoading: true
    });

    this.postSupportForm(payload)
      .then(() => {
        this.setState({
          formSubmitted: true,
          isSuccess: true,
          isLoading: false
        });
        this.sendSupportAnalyticsData(true);
      })
      .catch(err => {
        this.setState({
          formSubmitted: true,
          isSuccess: false,
          isLoading: false
        });
        this.sendSupportAnalyticsData(false);
        log.error('Error submitting support form. ', err);
      });
  };

  followUpPayload = values => {
    const { threadId, leapRequestId, tabValue } = this.props;
    const { comments } = values;
    const { shortname: region } = updateSessionStorage.getItem(SELECTED_REGION);
    const { fullname: accountName } = updateSessionStorage.getItem(SELECTED_ACCOUNT);
    const { lang } = getAppConfig('locale');
    const formatBasedOnLocale = (lang.toLowerCase() === 'en' && 'MM/DD/YYYY') || 'DD/MM/YYYY';
    const payload = {
      followUpDetail: comments,
      creationDate: moment().format(formatBasedOnLocale),
      region,
      accountName,
      caseThreadID: threadId
    };
    this.setState({
      isLoading: true
    });

    this.postSupportForm(payload)
      .then(() => {
        this.setState({
          formSubmitted: true,
          isSuccess: true,
          isLoading: false
        });
        this.sendSupportAnalyticsData(true);
      })
      .catch(err => {
        this.setState({
          formSubmitted: true,
          isSuccess: false,
          isLoading: false
        });
        log.error('Error submitting fo form. ', err);
        if (leapRequestId) {
          const isOpenOrClose = tabValue ? types.MY_REQUESTS_CLOSED_TAB : types.MY_REQUESTS_OPEN_TAB;
          const analyticsData = {
            description: types.MY_REQUESTS_FOLLOW_UP_ERROR.replace('{##}', isOpenOrClose),
            pageName: `${types.MY_REQUESTS_SUPPORT_URL.replace('{##}', isOpenOrClose)}:error`,
            capability: types.MY_REQUESTS_CAPABILITY,
            process: { name: types.MY_REQUESTS_PROCESS_NAME },
            event: types.MY_REQUESTS_PROCESS_ERROR,
            form: { transactionID: leapRequestId }
          };
          PubSub.publish(types.MY_REQUESTS_ANALYTICS, analyticsData);
        }
      });
  };

  /**
   * @method createValidationSchema
   * @description create a validation schema to pass in formik
   * @returns validation schema for ask aquestion form
   */
  createValidationSchema = () => {
    const { leapRequestId } = this.props;
    const {
      messages: {
        regionValMessage,
        productSegmentValMessage,
        countryValMessage,
        supportTopicValMessage,
        secondaryEmail,
        comments,
        priorityValMessage,
        supportTeamsValMessage
      }
    } = this.props;
    const { isWebToCase, showPriority } = this.state;
    const { country = '' } = getAppConfig('locale');
    let validationObj = leapRequestId
      ? { comments: Yup.string().required(comments) }
      : {
          productSegments: Yup.string().required(productSegmentValMessage),
          countries: Yup.string().required(countryValMessage),
          supportTopics: Yup.string().required(supportTopicValMessage),
          phone: Yup.string(),
          comments: Yup.string().required(comments),
          priority: isWebToCase && showPriority ? Yup.string().required(priorityValMessage) : Yup.string(),
          supportTeams: isWebToCase && showPriority ? Yup.string().required(supportTeamsValMessage) : Yup.string(),
          orderNumber: Yup.string(),
          purchaseOrderNumber: Yup.string(),
          secondaryEmail: Yup.string().email(secondaryEmail)
        };
    if (!leapRequestId && country.toLowerCase() !== types.BRAZIL) {
      validationObj = { ...validationObj, regions: Yup.string().required(regionValMessage) };
    }
    return Yup.object().shape(validationObj);
  };

  /**
   * @method handleChange
   * @description handle change in textbox
   * @returns updated state of comments field
   */
  handleChange = setFieldValue => {
    const changeHandler = setFieldValue;
    return event => {
      const target = event.currentTarget;
      const { value } = target;
      const id = target.getAttribute('id');
      this.setState(prevState => ({ ...prevState, formState: { ...prevState.formState, [id]: value } }));
      changeHandler(id, value, false);
    };
  };

  /**
   * @method handleDropdownChange
   * @description handle nested mapping dropdown
   * @returns updated state selected drop down field and enable next dropdown
   */
  handleDropdownChange = (selectedOption, key, setFieldValueCallBack) => {
    const { formOptions } = this.state;
    const selectedIndex = formOptions.findIndex(item => item.id === key);
    const { routedType = '' } = selectedOption;
    let autoSelectDropDown = null;
    let nextDropDownOptions;
    this.setState(
      prevState => ({
        ...prevState,
        isWebToCase: key === SUPPORT_TEAMS || routedType === 'Create Case',
        showPriority: key === SUPPORT_TOPICS ? !!selectedOption.supportTeams : true,
        formState: {
          ...prevState.formState,
          [formOptions[selectedIndex].id]: selectedOption.value
        },
        formOptions: formOptions.map((item, index) => {
          if (index > selectedIndex + 1 && key !== SUPPORT_TEAMS) {
            return {
              ...item,
              options: [],
              value: '',
              disable: true
            };
          }
          if (item.id === key) {
            nextDropDownOptions = key !== SUPPORT_TEAMS && selectedOption[formOptions[selectedIndex + 1].id];
            return {
              ...item,
              value: selectedOption.value
            };
          }
          if (index === selectedIndex + 1 && key !== SUPPORT_TEAMS) {
            if (nextDropDownOptions && nextDropDownOptions.length === 1) {
              autoSelectDropDown = {
                item: nextDropDownOptions[0],
                key: item.id
              };
            }
            return {
              ...item,
              disable: false,
              value: nextDropDownOptions && nextDropDownOptions.length === 1 ? nextDropDownOptions[0].value : '',
              options: nextDropDownOptions || []
            };
          }
          return item;
        })
      }),
      () => {
        if (autoSelectDropDown && autoSelectDropDown.key !== SUPPORT_TEAMS) {
          if (setFieldValueCallBack) {
            setFieldValueCallBack(autoSelectDropDown.key, autoSelectDropDown.item.value, false);
          }
          this.handleDropdownChange(autoSelectDropDown.item, autoSelectDropDown.key, setFieldValueCallBack);
        }
      }
    );
  };

  /**
   * @method handlepriorityChange
   * @description updated static priority drop down state
   * @returns updated state of priority field
   */
  handlepriorityChange = selectedOption =>
    this.setState(prevState => ({
      ...prevState,
      formState: {
        ...prevState.formState,
        priority: selectedOption.value
      },
      selectedPriority: selectedOption
    }));

  /**
   * @method checkIsWebToCase
   * @description This function is used to check if we have Web to case or Email case
   * @returns true/false whether isWebToCase or not
   */
  checkIsWebToCase = (item, isWebToCase, showPriority) => {
    if (item.id === SUPPORT_TEAMS && (!isWebToCase || !showPriority)) {
      return false;
    }
    return true;
  };

  /**
   * @method handleReturnClick
   * @description in cacse of error takes user back to edit form state on clicck of return
   * @returns updated formsubmit state
   */
  handleReturnClick = () => {
    this.setState({ formSubmitted: false });
  };

  /**
   * @method postSupportForm
   * @description call support form api to send user seleccted data on support form
   * @returns general support api call
   */
  postSupportForm = async payload => {
    const {
      Post,
      urls: { generalSupportUrl, getRequestsUrl },
      leapRequestId
    } = this.props;
    const endpoint = leapRequestId ? `${getRequestsUrl}/followup` : generalSupportUrl;
    return Post(endpoint, payload, true);
  };

  /**
   * @method sendSupportAnalyticsData
   * @description send analytics data that is seleccted by user in support form
   * @returns updated datalayer value
   */
  sendSupportAnalyticsData = status => {
    const { leapRequestId, tabValue } = this.props;
    let analyticsData = {};
    if (leapRequestId && status) {
      const isOpenOrClose = tabValue ? types.MY_REQUESTS_CLOSED_TAB : types.MY_REQUESTS_OPEN_TAB;
      analyticsData = {
        description: types.MY_REQUESTS_FORM_SUBMIT_DESCP.replace('{##}', isOpenOrClose),
        pageName: types.MY_REQUESTS_SUPPORT_URL.replace('{##}', isOpenOrClose),
        capability: types.MY_REQUESTS_CAPABILITY,
        element: { name: types.MY_REQUESTS_FORM_SUBMIT_EVENT },
        process: { name: types.MY_REQUESTS_PROCESS_NAME },
        event: types.MY_REQUESTS_CLICK_ACTION,
        form: { transactionID: leapRequestId }
      };
    } else if (status) {
      analyticsData = {
        description: types.SUPPORT_COMPLETE_DESC,
        event: types.SUPPORT_COMPLETE_EVENT
      };
    } else {
      analyticsData = {
        description: types.SUPPORT_PROCESS_ERROR_DESC,
        event: types.PROCESS_ERROR_EVENT
      };
    }
    const analyticsEvent = leapRequestId ? types.MY_REQUESTS_ANALYTICS : types.SUPPORT_EVENT;
    PubSub.publish(analyticsEvent, analyticsData);
  };

  /**
   * @method renderChild
   * @description render ask A Question form
   * @returns JSX for askAQuestionForm
   */
  renderChild = (values, errors, handleSubmit, setFieldValue, renderChildData) => {
    const {
      controls: {
        maxEmailLength,
        maxOrderNumberLength,
        maxPurchaseOrderNumberLength,
        maxPhoneNumberLength,
        maxLengthComments
      },
      leapRequestId
    } = this.props;
    const {
      labels: {
        cancelBtn,
        comments,
        commentPlaceholder,
        disclaimer,
        disclaimerPolicy,
        email,
        secondaryEmail,
        phone,
        phonePlaceholder,
        submitBtn,
        policyUrl,
        orderNumber,
        orderNumberPlaceholder,
        purchaseOrderNumber,
        purchaseOrderNumberPlaceholder,
        secondaryEmailPlaceholder,
        orderNumberToolTip
      },
      priority
    } = renderChildData;
    const { selectedPriority, isWebToCase, showPriority } = this.state;
    const changeHandler = this.handleChange(setFieldValue);
    const conditionalClass = isWebToCase ? 'show-field' : 'hide-field';
    return (
      <form className="ask-a-question-form" onSubmit={handleSubmit}>
        {!leapRequestId && (
          <div className="ask-a-question-wrapper">
            {this.getFormFields(errors, setFieldValue, renderChildData)}
            {isWebToCase && showPriority && (
              <div className={`form-input-col-2 ${conditionalClass}`}>
                <Label className="question-form-input-label required" htmlFor="priority">
                  {priority.label}
                </Label>
                <CustomSelect
                  id="priority"
                  options={priority.options}
                  classes={`question-form-select ${this.getErrorInfo('priority', errors)}`}
                  value={{ label: selectedPriority.label || 'Select...', value: selectedPriority.value }}
                  onChange={selectedOption => {
                    this.handlepriorityChange(selectedOption);
                    setFieldValue('priority', selectedOption.value, false);
                  }}
                />
                {errors.priority && <FormValidationError errorMessage={errors.priority} />}
              </div>
            )}
          </div>
        )}

        {!leapRequestId && isWebToCase && (
          <div className={`ask-a-question-wrapper ${conditionalClass}`}>
            <>
              <div className="form-input-col-2">
                <div className="order-number-label">
                  <Label className="question-form-input-label" htmlFor="orderNumber">
                    {orderNumber}
                  </Label>
                  <CustomIcon
                    data-tooltip-id="tooltip-order-num"
                    data-tooltip-content={orderNumberToolTip}
                    type="help"
                    size="16"
                  />
                  <Tooltip identifier="tooltip-order-num" />
                </div>
                {this.getTextInput(
                  'orderNumber',
                  values,
                  errors,
                  orderNumberPlaceholder,
                  maxOrderNumberLength,
                  setFieldValue
                )}
              </div>
              <div className="form-input-col-2">
                <Label className="question-form-input-label" htmlFor="purchaseOrderNumber">
                  {purchaseOrderNumber}
                </Label>
                {this.getTextInput(
                  'purchaseOrderNumber',
                  values,
                  errors,
                  purchaseOrderNumberPlaceholder,
                  maxPurchaseOrderNumberLength,
                  setFieldValue
                )}
              </div>
            </>
          </div>
        )}

        {!leapRequestId && (
          <div className="ask-a-question-wrapper">
            <div className="form-input-col-2">
              <Label className="question-form-input-label required" htmlFor="email">
                {email}
              </Label>
              {this.getTextInput('email', values, errors, '', maxEmailLength, setFieldValue, true)}
            </div>
            <div className="form-input-col-2">
              {isWebToCase && (
                <>
                  <Label className="question-form-input-label" htmlFor="email">
                    {secondaryEmail}
                  </Label>
                  {this.getTextInput(
                    'secondaryEmail',
                    values,
                    errors,
                    secondaryEmailPlaceholder,
                    maxEmailLength,
                    setFieldValue
                  )}
                  {errors.secondaryEmail && <FormValidationError errorMessage={errors.secondaryEmail} />}
                </>
              )}
            </div>
            <div className="form-input-col-2">
              <Label className="question-form-input-label" htmlFor="phone">
                {phone}
              </Label>
              {this.getTextInput('phone', values, errors, phonePlaceholder, maxPhoneNumberLength, setFieldValue)}
            </div>
          </div>
        )}

        <div className="ask-a-question-wrapper">
          <div className="form-input-item">
            <Label
              className={leapRequestId ? 'follow-up-comments zeta b required' : 'question-form-input-label required'}
              htmlFor="comments"
            >
              {comments}
            </Label>
            <textarea
              rows="4"
              placeholder={commentPlaceholder}
              id="comments"
              maxLength={maxLengthComments}
              className={`comments ${this.getErrorInfo('comments', errors)}`}
              value={values.comments}
              onChange={changeHandler}
            />
            {errors.comments && <FormValidationError errorMessage={errors.comments} />}
          </div>
        </div>
        <div className="disclaimer">
          <p>
            {`${disclaimer} `}
            <a href={DOMPurify.sanitize(policyUrl)} target="_blank" rel="noopener noreferrer">
              {disclaimerPolicy}
            </a>
          </p>
        </div>
        <div className="ask-a-question-btn-wrapper">
          <Button
            type="button"
            handleClick={this.handleFormCancel}
            classes="btn btn-light button cancel-btn"
            buttonLabel={cancelBtn}
          >
            {cancelBtn || 'Cancel'}
          </Button>
          <Button
            type="submit"
            classes="btn btn-color"
            buttonLabel={submitBtn}
            data-metrics-title="submit-support"
            dataMetricsLocation="SS"
          >
            {submitBtn}
          </Button>
        </div>
      </form>
    );
  };

  /**
   * @method renderContent
   * @description passing desired prop to Formik container for general support form
   * @returns Formik JSX
   */
  renderContent = () => {
    const { labels, priority } = this.props;
    const { formState } = this.state;

    const renderChildData = {
      labels,
      priority
    };
    return (
      <>
        <Formik
          initialValues={formState}
          validationSchema={this.createValidationSchema()}
          onSubmit={values => this.onSubmit(values)}
        >
          {({ values, errors, handleSubmit, setFieldValue }) =>
            this.renderChild(values, errors, handleSubmit, setFieldValue, renderChildData)
          }
        </Formik>
      </>
    );
  };

  /**
   * @method render
   * @description Renders the AskAQuestionForm DOM element
   * @returns {*} Rendered component
   */
  render() {
    const {
      labels: { title, successHeader, failureHeader, successButtonLabel, failureButtonLabel, id = '' },
      messages,
      leapRequestId
    } = this.props;
    const formSubmittionLabels = {
      messages: {
        successMessage: messages.successMessage,
        failureMessage: messages.failureMessage
      },
      labels: {
        successHeader,
        failureHeader,
        successButtonLabel,
        failureButtonLabel
      }
    };
    const { formState, formSubmitted, isSuccess, isLoading } = this.state;
    return (
      <div className="ask-a-question-form-container">
        {!formSubmitted ? (
          <>
            <div className="ask-a-question-form-header">
              <Title classes="question-form-title bold" title={title} />
              {leapRequestId && <p>{`${id} ${leapRequestId}`}</p>}
            </div>
            {formState.email && this.renderContent()}
          </>
        ) : (
          <FormSubmissionPage
            {...formSubmittionLabels}
            handleCloseModal={this.handleFormSucess}
            isSuccess={isSuccess}
            onReturn={this.handleReturnClick}
          />
        )}
        {isLoading && <Loader isLayoutLoader />}
      </div>
    );
  }
}

/**
 * @property AskAQuestionForm PropsTypes
 */
AskAQuestionForm.propTypes = {
  handleCloseModal: PropTypes.func,
  labels: PropTypes.shape({
    title: PropTypes.string,
    successHeader: PropTypes.string,
    failureHeader: PropTypes.string,
    successButtonLabel: PropTypes.string,
    failureButtonLabel: PropTypes.string,
    orderNumberToolTip: PropTypes.string,
    id: PropTypes.string
  }),
  messages: PropTypes.shape({
    successMessage: PropTypes.string,
    failureMessage: PropTypes.string,
    regionValMessage: PropTypes.string,
    productSegmentValMessage: PropTypes.string,
    countryValMessage: PropTypes.string,
    supportTopicValMessage: PropTypes.string,
    secondaryEmail: PropTypes.string,
    comments: PropTypes.string,
    priorityValMessage: PropTypes.string,
    supportTeamsValMessage: PropTypes.string
  }),
  supportTopics: PropTypes.shape({
    options: PropTypes.instanceOf(Array)
  }),
  productSegment: PropTypes.shape({
    options: PropTypes.instanceOf(Array)
  }),
  controls: PropTypes.shape({
    maxEmailLength: PropTypes.string,
    maxPhoneNumberLength: PropTypes.number,
    maxOrderNumberLength: PropTypes.number,
    maxPurchaseOrderNumberLength: PropTypes.number,
    maxLengthComments: PropTypes.number
  }),
  priority: PropTypes.instanceOf(Object).isRequired,
  getUserInfoFromOkta: PropTypes.func.isRequired,
  userContext: PropTypes.instanceOf(Object),
  Post: PropTypes.func.isRequired,
  Get: PropTypes.func.isRequired,
  urls: PropTypes.instanceOf(Object),
  leapRequestId: PropTypes.string,
  threadId: PropTypes.string,
  tabValue: PropTypes.number,
  getFollowUpData: PropTypes.func
};

/**
 * @property AskAQuestionForm defaultProps
 */
AskAQuestionForm.defaultProps = {
  handleCloseModal: () => {},
  urls: {},
  labels: {
    title: '',
    successHeader: '',
    failureHeader: '',
    successButtonLabel: '',
    failureButtonLabel: '',
    orderNumberToolTip: '',
    id: ''
  },
  userContext: {},
  messages: {
    successMessage: '',
    failureMessage: '',
    regionValMessage: '',
    productSegmentValMessage: '',
    countryValMessage: '',
    supportTopicValMessage: '',
    secondaryEmail: '',
    comments: '',
    priorityValMessage: '',
    supportTeamsValMessage: ''
  },
  supportTopics: {
    options: []
  },
  productSegment: {
    options: []
  },
  controls: {
    maxEmailLength: '80',
    maxPhoneNumberLength: 40,
    maxOrderNumberLength: 12,
    maxPurchaseOrderNumberLength: 20,
    maxLengthComments: 1000
  },
  leapRequestId: '',
  threadId: '',
  tabValue: 0,
  getFollowUpData: () => {}
};
export default AskAQuestionForm;
